import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { API } from "../lib/api";
import Header from "../components/app/Header";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import MermaidBlock, { ZoomableMedia } from "../components/app/MermaidBlock";
import BusinessLogo from "../components/app/BusinessLogo";
import {
  ArrowLeft,
  Send,
  Plus,
  MessageSquare,
  Lock,
  Sparkles,
  Trash2,
  Download,
  Loader2,
  ImagePlus,
  X,
  Copy,
} from "lucide-react";
import { toast } from "sonner";

// Downscale + JPEG-encode a File to a data URL under ~1.6MB so we can inline
// it in a JSON POST without hitting proxy limits. Preserves aspect ratio.
async function fileToCompressedDataUrl(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.onload = () => {
      img.onerror = () => reject(new Error("Failed to decode image"));
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ChatPage() {
  const { toolId } = useParams();
  const [tool, setTool] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  // PostCraft / vision: user can attach a single image to the next message.
  const [attachedImage, setAttachedImage] = useState(null); // data URL string
  const [attaching, setAttaching] = useState(false);
  const fileInputRef = useRef(null);
  // Only Gemini-backed tools currently support the image attach flow.
  const supportsImages = (tool?.model || "").toLowerCase().startsWith("gemini-");
  // PostCraft auto-brands generated posts with the user's uploaded logo.
  const isPostCraft = tool?.name === "PostCraft";
  const [logoUrl, setLogoUrl] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // The user MUST click a conversation starter before they can type — but
  // only when this is a fresh, empty chat that actually has starters defined.
  // Once any message exists in the conversation, the input unlocks normally.
  const hasStarters = (tool?.conversation_starters || []).length > 0;
  // Once any message exists, or the user has staged either an attachment or
  // typed / pre-filled input (via the product-photo starter shortcut), the
  // composer is unlocked. Otherwise the user must click a starter first.
  const mustPickStarter =
    hasStarters && messages.length === 0 && !attachedImage && !(input || "").trim();

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get(`/tools/${toolId}`),
      api.get("/sessions", { params: { tool_id: toolId } }),
    ])
      .then(([t, s]) => {
        if (!mounted) return;
        setTool(t.data);
        setSessions(s.data);
        if (s.data.length > 0) {
          setActiveSessionId(s.data[0].id);
        }
      })
      .catch(() => toast.error("Failed to load tool"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [toolId]);

  // Load the user's business logo whenever this becomes a PostCraft session
  // (the compact pill and Nano Banana auto-branding both depend on it).
  useEffect(() => {
    if (!isPostCraft) return;
    let mounted = true;
    api
      .get("/auth/me")
      .then((r) => {
        if (mounted) setLogoUrl(r.data?.business_logo_url || null);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [isPostCraft]);

  useEffect(() => {
    if (!activeSessionId) {
      setMessages([]);
      return;
    }
    api
      .get(`/sessions/${activeSessionId}/messages`)
      .then((r) => setMessages(r.data))
      .catch(() => toast.error("Failed to load messages"));
  }, [activeSessionId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, sending]);

  const startNewChat = () => {
    setActiveSessionId(null);
    setMessages([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const sendMessage = async (e, overrideText) => {
    e?.preventDefault();
    const text = (overrideText ?? input).trim();
    // Allow empty text if an image is attached (e.g. the very first PostCraft
    // turn where the user just uploads the product photo and hits send).
    if ((!text && !attachedImage) || sending) return;
    if (!tool?.has_access) {
      toast.error("This GPT is locked. Please unlock access.");
      return;
    }
    if (!overrideText) setInput("");
    setSending(true);
    // Snapshot & clear the attached image now — the user can compose their
    // next message while we're still streaming.
    const outgoingImage = attachedImage;
    setAttachedImage(null);
    const tempUserId = `tmp-u-${Date.now()}`;
    const tempAiId = `tmp-a-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: tempUserId, role: "user", content: text, timestamp: new Date().toISOString(), image_url: outgoingImage || undefined },
      { id: tempAiId, role: "assistant", content: "", timestamp: new Date().toISOString(), streaming: true, images: [] },
    ]);
    const token = localStorage.getItem("ai_hub_token");
    let buffered = "";
    let finalUserMsgId = null;
    let finalAiMsgId = null;
    let newSessionId = activeSessionId;
    try {
      const resp = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          tool_id: toolId,
          session_id: activeSessionId,
          message: text,
          image_base64: outgoingImage || undefined,
        }),
      });
      if (!resp.ok || !resp.body) {
        const errBody = await resp.text();
        let detail = "Failed to send message";
        try { detail = JSON.parse(errBody).detail || detail; } catch { /* body not JSON; keep default detail */ }
        throw new Error(detail);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let sseBuf = "";
      // Read stream
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        sseBuf += decoder.decode(value, { stream: true });
        // Each event is "data: {...}\n\n"
        let idx;
        while ((idx = sseBuf.indexOf("\n\n")) >= 0) {
          const raw = sseBuf.slice(0, idx).trim();
          sseBuf = sseBuf.slice(idx + 2);
          if (!raw.startsWith("data:")) continue;
          const json = raw.slice(5).trim();
          if (!json) continue;
          let evt;
          try { evt = JSON.parse(json); } catch { continue; }
          if (evt.type === "session") {
            newSessionId = evt.session_id;
            finalUserMsgId = evt.user_message_id;
            finalAiMsgId = evt.assistant_message_id;
          } else if (evt.type === "queued") {
            // PostCraft concurrency queue — server tells us our slot position.
            // 0 = starting now, >0 = waiting in line. We surface this on the
            // assistant bubble so the user sees a live status and doesn't
            // spam "new conversation" (which is also disabled while sending).
            setMessages((m) =>
              m.map((x) =>
                x.id === tempAiId
                  ? {
                      ...x,
                      queue_status: {
                        position: evt.position || 0,
                        concurrency: evt.concurrency || 5,
                        message: evt.message || "",
                      },
                    }
                  : x
              )
            );
          } else if (evt.type === "delta") {
            buffered += evt.content || "";
            // Hide the PostCraft `<GENERATE_IMAGES>...</GENERATE_IMAGES>`
            // handoff marker from the live-streamed bubble. Once the opening
            // tag is seen we truncate everything from there until either the
            // closing tag arrives (then we strip the whole block) or the
            // stream ends.
            let display = buffered;
            const openIdx = display.indexOf("<GENERATE_IMAGES>");
            if (openIdx !== -1) {
              display = display
                .replace(/<GENERATE_IMAGES>[\s\S]*?<\/GENERATE_IMAGES>/g, "")
                .replace(/<GENERATE_IMAGES>[\s\S]*$/, "")
                .trimEnd();
            }
            setMessages((m) =>
              m.map((x) =>
                x.id === tempAiId ? { ...x, content: display } : x
              )
            );
          } else if (evt.type === "image") {
            // Nano Banana variation arrived — slot it into the assistant
            // bubble at its variation index (1-based) so the ordering is
            // stable regardless of arrival order.
            setMessages((m) =>
              m.map((x) => {
                if (x.id !== tempAiId) return x;
                const arr = Array.from(x.images || []);
                const idx = Math.max(0, (evt.variation || arr.length + 1) - 1);
                arr[idx] = evt.url;
                return { ...x, images: arr };
              })
            );
          } else if (evt.type === "image_pending") {
            // On-demand variation (V2/V3, and V1 when auto-gen failed).
            // Store the prompt + aspect ratio on the assistant message so
            // the "Generate this variation" button can call /generate-image
            // when the user taps it.
            setMessages((m) =>
              m.map((x) => {
                if (x.id !== tempAiId) return x;
                const pending = { ...(x.pending_variations || {}) };
                const v = evt.variation || 0;
                if (v >= 1 && v <= 3) {
                  pending[v] = {
                    image_prompt: evt.image_prompt || "",
                    aspect_ratio: evt.aspect_ratio || "1:1",
                    message_id: evt.message_id || null,
                    status: evt.auto_failed ? "failed" : "on_demand",
                  };
                }
                return { ...x, pending_variations: pending };
              })
            );
          } else if (evt.type === "error") {
            throw new Error(evt.message || "AI provider error");
          }
        }
      }
      // Finalize ids and clear streaming flag. Also strip the PostCraft
      // `<GENERATE_IMAGES>...</GENERATE_IMAGES>` handoff marker from the
      // final rendered text — the backend already strips it from the
      // persisted copy, but the raw block was streamed via delta events
      // and would otherwise render as literal text in the chat bubble.
      const finalContent = buffered
        .replace(/<GENERATE_IMAGES>[\s\S]*?<\/GENERATE_IMAGES>/g, "")
        .trimEnd();
      setMessages((m) =>
        m.map((x) => {
          if (x.id === tempUserId && finalUserMsgId)
            return { ...x, id: finalUserMsgId };
          if (x.id === tempAiId)
            return { ...x, id: finalAiMsgId || x.id, content: finalContent, streaming: false };
          return x;
        })
      );
      if (!activeSessionId && newSessionId) {
        setActiveSessionId(newSessionId);
        const s = await api.get("/sessions", { params: { tool_id: toolId } });
        setSessions(s.data);
      }
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== tempUserId && x.id !== tempAiId));
      toast.error(err?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (activeSessionId === sid) {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch {
      toast.error("Failed to delete");
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadSessionPdf = async (sid) => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    const toastId = toast.loading(
      "Crafting your deliverable PDF... this can take up to a minute.",
    );
    try {
      const token = localStorage.getItem("ai_hub_token");
      // Generation can take ~30-60s for richer deliverables; give the
      // browser fetch plenty of headroom.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180_000);
      const res = await fetch(`${API}/sessions/${sid}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `deliverable-${sid.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Deliverable PDF downloaded", { id: toastId });
    } catch (err) {
      toast.error(
        err?.name === "AbortError"
          ? "PDF took too long — please try again."
          : "Could not download PDF",
        { id: toastId },
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const showLocked = tool && !tool.has_access;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header />
      <div className="flex-1 min-h-0 max-w-7xl mx-auto w-full px-4 md:px-10 py-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 overflow-hidden">
        {/* Sidebar (sticky) */}
        <aside className="hidden lg:flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0">
          <div className="p-4 border-b border-zinc-900">
            <Link
              to="/dashboard"
              data-testid="back-to-dashboard"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white"
            >
              <ArrowLeft className="w-3 h-3" /> Dashboard
            </Link>
            <div className="mt-3">
              <h2 className="font-display text-xl tracking-tight font-bold text-white truncate">
                {tool?.name || "..."}
              </h2>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mt-1">
                {tool?.category}
              </div>
            </div>
            <button
              data-testid="new-chat-btn"
              onClick={startNewChat}
              disabled={sending}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg px-4 py-2.5 transition-colors glow-primary disabled:glow-none"
              title={sending ? "Please wait — your current message is still generating" : ""}
            >
              <Plus className="w-4 h-4" /> {sending ? "Generating…" : "New conversation"}
            </button>
            {isPostCraft && (
              <div className="mt-3">
                <BusinessLogo logoUrl={logoUrl} onChange={setLogoUrl} compact />
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 px-2 py-2">
              History
            </div>
            {sessions.length === 0 && (
              <div className="text-xs text-zinc-600 px-2 py-4">
                No conversations yet.
              </div>
            )}
            {sessions.map((s) => (
              <div
                key={s.id}
                data-testid={`session-${s.id}`}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                  activeSessionId === s.id
                    ? "bg-emerald-500/10 border border-emerald-500/20"
                    : "hover:bg-zinc-900 border border-transparent"
                }`}
                onClick={() => setActiveSessionId(s.id)}
              >
                <MessageSquare
                  className={`w-3.5 h-3.5 shrink-0 ${
                    activeSessionId === s.id ? "text-emerald-400" : "text-zinc-500"
                  }`}
                />
                <span className="text-xs text-zinc-300 truncate flex-1">
                  {s.title}
                </span>
                <button
                  data-testid={`download-session-${s.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadSessionPdf(s.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-emerald-400 transition-opacity"
                  title="Download PDF"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSession(s.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-rose-400 transition-opacity"
                  title="Delete"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* Chat area */}
        <section className="flex flex-col border border-zinc-900 rounded-2xl bg-zinc-950/40 overflow-hidden min-h-0">
          {showLocked ? (
            <LockedView tool={tool} />
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              Loading...
            </div>
          ) : (
            <>
              <div className="px-4 md:px-6 py-3 border-b border-zinc-900 flex items-center justify-between bg-zinc-950/60">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 truncate">
                  {activeSessionId
                    ? sessions.find((s) => s.id === activeSessionId)?.title ||
                      "Conversation"
                    : "New conversation"}
                </div>
                {activeSessionId && messages.length > 0 && (
                  <button
                    data-testid="download-current-pdf"
                    onClick={() => downloadSessionPdf(activeSessionId)}
                    disabled={downloadingPdf}
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-md transition-colors"
                    title="Generate a comprehensive deliverable PDF (takes ~30-60s)"
                  >
                    {downloadingPdf ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Crafting PDF...
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5" /> Download PDF
                      </>
                    )}
                  </button>
                )}
              </div>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto p-6 md:p-10 space-y-6"
              >
                {messages.length === 0 && !sending && (
                  <EmptyChat
                    tool={tool}
                    onStarter={(text) => {
                      // The "I've got a product photo" starter should trigger
                      // the image-upload flow ONCE — not send a text-only
                      // message that would then force the LLM to ask "please
                      // attach your product image". Detect the product-photo
                      // starter, open the file picker, and pre-fill the input
                      // with a canned message so when the user hits Send the
                      // LLM receives image + text on the very first turn.
                      const isProductStarter =
                        supportsImages &&
                        /product\s*photo|got a product|have a product/i.test(text || "");
                      if (isProductStarter) {
                        setInput("Here's my product — help me craft a post.");
                        // Focus and open file picker on the next tick so React
                        // has time to render the composer.
                        setTimeout(() => {
                          try { fileInputRef.current && fileInputRef.current.click(); } catch { /* ignore */ }
                          try { inputRef.current && inputRef.current.focus(); } catch { /* ignore */ }
                        }, 60);
                        return;
                      }
                      sendMessage(null, text);
                    }}
                  />
                )}
                {messages.map((m) => (
                  <Bubble
                    key={m.id}
                    msg={m}
                    activeSessionId={activeSessionId}
                    setMessages={setMessages}
                  />
                ))}
              </div>

              <form
                onSubmit={sendMessage}
                className="border-t border-zinc-900 p-4 md:p-5 flex flex-col gap-3 bg-zinc-950/60"
              >
                {attachedImage && (
                  <div
                    data-testid="chat-attachment-preview"
                    className="self-start relative inline-flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-2 pr-3"
                  >
                    <img
                      src={attachedImage}
                      alt="attached"
                      className="w-14 h-14 rounded-lg object-cover border border-zinc-800"
                    />
                    <span className="text-xs text-zinc-400">Image attached</span>
                    <button
                      type="button"
                      data-testid="chat-attachment-remove"
                      onClick={() => setAttachedImage(null)}
                      className="ml-1 w-6 h-6 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-300"
                      aria-label="Remove attachment"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-3">
                  {supportsImages && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = "";
                          if (!file) return;
                          if (!file.type.startsWith("image/")) {
                            toast.error("Please pick an image file.");
                            return;
                          }
                          setAttaching(true);
                          try {
                            const dataUrl = await fileToCompressedDataUrl(file);
                            setAttachedImage(dataUrl);
                          } catch {
                            toast.error("Could not read that image.");
                          } finally {
                            setAttaching(false);
                          }
                        }}
                      />
                      <button
                        type="button"
                        data-testid="chat-attach-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={mustPickStarter || sending || attaching}
                        className="inline-flex items-center justify-center bg-zinc-900 border border-zinc-800 hover:border-emerald-500/40 text-zinc-300 hover:text-white rounded-xl w-12 h-12 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Attach product image"
                        aria-label="Attach product image"
                      >
                        {attaching ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ImagePlus className="w-4 h-4" />
                        )}
                      </button>
                    </>
                  )}
                  <textarea
                    ref={inputRef}
                    data-testid="chat-input"
                    rows={1}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={mustPickStarter}
                    placeholder={
                      mustPickStarter
                        ? "Pick a conversation starter above to begin..."
                        : `Message ${tool?.name || "agent"}...`
                    }
                    className="flex-1 bg-zinc-900/60 border border-zinc-800 rounded-xl px-4 py-3 text-[15px] text-white outline-none focus:border-emerald-500/50 resize-none max-h-40 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    data-testid="chat-send-btn"
                    type="submit"
                    disabled={sending || mustPickStarter || (!input.trim() && !attachedImage)}
                    className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl w-12 h-12 transition-colors glow-primary"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

const COUNT_MERMAID = (s) => (s ? (s.match(/```mermaid\b/g) || []).length : 0);

const Bubble = ({ msg, activeSessionId, setMessages }) => {
  const isUser = msg.role === "user";
  const contentRef = useRef(null);
  const expectedMermaid = isUser ? 0 : COUNT_MERMAID(msg.content);
  // For assistant messages: stay hidden until streaming is done AND every
  // expected mermaid SVG has finished rendering. This eliminates the layout
  // jump where the text appears first and the diagram pops in 300-600ms later.
  const [ready, setReady] = useState(
    isUser || (!msg.streaming && expectedMermaid === 0)
  );

  useEffect(() => {
    if (ready || isUser) return;
    if (msg.streaming) return; // wait for the stream to finish first
    if (expectedMermaid === 0) {
      // No mermaid blocks expected — reveal immediately on next tick so
      // react finishes mounting the markdown first.
      const t = setTimeout(() => setReady(true), 0);
      return () => clearTimeout(t);
    }
    let cancelled = false;
    const start = Date.now();
    const check = () => {
      if (cancelled) return;
      const got = contentRef.current?.querySelectorAll(".mermaid-svg svg").length || 0;
      // Either every mermaid finished, OR we've been waiting > 10s (fall back
      // to showing the message even if mermaid failed silently somehow).
      if (got >= expectedMermaid || Date.now() - start > 10_000) {
        setReady(true);
      } else {
        setTimeout(check, 80);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [msg.streaming, msg.content, expectedMermaid, ready, isUser]);

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div
          data-testid={`msg-user-${msg.id}`}
          className="max-w-[85%] flex flex-col items-end gap-2"
        >
          {msg.image_url && (
            <ZoomableMedia label="attached image">
              <img
                src={msg.image_url}
                alt="attached"
                className="rounded-2xl border border-zinc-800 max-w-[240px] max-h-[240px] object-cover"
              />
            </ZoomableMedia>
          )}
          {msg.content && (
            <div className="bg-zinc-800/60 border border-zinc-800 rounded-2xl rounded-br-sm px-5 py-3 text-[15px] leading-relaxed text-white whitespace-pre-wrap">
              {msg.content}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 shrink-0 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mt-1">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
      </div>
      <div
        data-testid={`msg-ai-${msg.id}`}
        className="beam-left pl-5 max-w-[85%] text-[15px] leading-relaxed text-zinc-100 markdown-body"
      >
        {!ready && (
          <span className="inline-flex items-center gap-2 py-1 text-zinc-500 text-sm">
            <span className="typing-dot"></span>
            <span className="typing-dot"></span>
            <span className="typing-dot"></span>
            <span className="text-xs uppercase tracking-[0.2em] ml-1">
              {msg.queue_status && msg.queue_status.position > 0
                ? `queued · #${msg.queue_status.position} in line`
                : (msg.streaming ? "thinking" : (expectedMermaid > 0 ? "drawing diagram" : "preparing"))}
            </span>
          </span>
        )}
        {msg.queue_status && msg.queue_status.position > 0 && !msg.content && (
          <div className="mt-2 text-[13px] leading-relaxed text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 max-w-md">
            High-demand queue engaged — your post is waiting for one of {msg.queue_status.concurrency} generation slots.
            <div className="text-[11px] text-amber-300/60 mt-1">Please keep this tab open. Starting new conversations is temporarily disabled so your job doesn&apos;t lose its place.</div>
          </div>
        )}
        {/* Off-screen render while mermaid is still building. We keep it in
            the React tree (display:none) so MermaidBlock's useEffect runs
            and mermaid.render() actually executes. Once `ready` flips,
            the same node is shown — no remount, no re-render of mermaid. */}
        <div
          ref={contentRef}
          style={ready ? undefined : { display: "none" }}
          aria-hidden={!ready}
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            // Allow `data:` image URIs for inline Nano Banana variations.
            // react-markdown v9+ strips them by default via defaultUrlTransform.
            urlTransform={(u) => u}
            components={{
              code({ inline, className, children, ...props }) {
                if (inline) {
                  return (
                    <code
                      className="px-1.5 py-0.5 rounded bg-zinc-800/80 border border-zinc-700/60 text-emerald-300 text-[0.9em] font-mono"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                }
                // ```mermaid blocks become interactive diagrams
                const lang = (className || "").replace("language-", "").trim();
                const raw = String(children || "").replace(/\n$/, "");
                if (lang === "mermaid") {
                  return <MermaidBlock code={raw} />;
                }
                return (
                  <pre className="my-3 rounded-lg bg-zinc-950 border border-zinc-800 p-4 overflow-x-auto text-[13px]">
                    <code className={`font-mono text-emerald-200 ${className || ""}`} {...props}>
                      {children}
                    </code>
                  </pre>
                );
              },
              img({ src, alt }) {
                // Custom scheme: `postcraft-v:N` → render the on-demand
                // "Generate this variation" button (or a retry button if
                // the auto-generation failed).
                if (typeof src === "string" && src.startsWith("postcraft-v:")) {
                  const n = parseInt(src.slice("postcraft-v:".length), 10);
                  return (
                    <PostCraftVariationSlot
                      variation={n}
                      pending={(msg.pending_variations || {})[n]}
                      messageId={msg.id}
                      sessionId={activeSessionId}
                      streaming={!!msg.streaming}
                      onImageReady={(url) => {
                        setMessages((mm) =>
                          mm.map((x) => {
                            if (x.id !== msg.id) return x;
                            const arr = Array.from(x.images || []);
                            while (arr.length < n) arr.push(null);
                            arr[n - 1] = url;
                            return { ...x, images: arr };
                          })
                        );
                      }}
                    />
                  );
                }
                return (
                  <ZoomableMedia label={alt || "image"}>
                    <img
                      src={src}
                      alt={alt || ""}
                      className="my-3 rounded-xl border border-zinc-800 max-w-full h-auto"
                      loading="lazy"
                    />
                  </ZoomableMedia>
                );
              },
              a({ children, ...props }) {
                return (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-300 underline decoration-emerald-500/40 hover:decoration-emerald-300"
                  >
                    {children}
                  </a>
                );
              },
              ul({ children }) {
                return <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>;
              },
              ol({ children }) {
                return <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>;
              },
              h1: ({ children }) => (
                <h1 className="font-display text-xl font-bold mt-3 mb-2 tracking-tight">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="font-display text-lg font-bold mt-3 mb-2 tracking-tight">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="font-display text-base font-bold mt-2 mb-1.5 tracking-tight">{children}</h3>
              ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-2 border-emerald-500/40 pl-3 my-2 text-zinc-300 italic">
                  {children}
                </blockquote>
              ),
              table: ({ children }) => (
                <ZoomableMedia label="Table">
                  <div className="my-3 overflow-x-auto">
                    <table className="min-w-full text-sm border border-zinc-800 rounded-lg overflow-hidden">{children}</table>
                  </div>
                </ZoomableMedia>
              ),
              th: ({ children }) => (
                <th className="text-left px-3 py-2 bg-zinc-900/60 border-b border-zinc-800 font-semibold">{children}</th>
              ),
              td: ({ children }) => (
                <td className="px-3 py-2 border-b border-zinc-900">{children}</td>
              ),
              p: ({ children }) => <p className="my-2">{children}</p>,
            }}
          >
            {(() => {
              // PostCraft: replace inline {{IMG_N}} markers with either the
              // generated image (once available) or a `postcraft-v:N` sentinel
              // that the custom img renderer below turns into a "Generate this
              // variation" button. Keeping everything inside markdown lets us
              // preserve the surrounding heading/caption layout.
              const raw = msg.content || "";
              if (!/\{\{IMG_\d+\}\}/.test(raw)) return raw;
              const imgs = msg.images || [];
              return raw.replace(/\{\{IMG_(\d+)\}\}/g, (_full, n) => {
                const idx = parseInt(n, 10) - 1;
                const url = imgs[idx];
                if (url) {
                  return `\n\n![Variation ${n}](${url})\n\n`;
                }
                // Sentinel — the custom img component detects the scheme and
                // renders the on-demand button (or a "generating…" state).
                return `\n\n![Variation ${n}](postcraft-v:${n})\n\n`;
              });
            })()}
          </ReactMarkdown>
          {msg.images && msg.images.length > 0 && !/\{\{IMG_\d+\}\}/.test(msg.content || "") && (
            <div
              data-testid="msg-ai-images"
              className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3"
            >
              {msg.images.map((url, i) => (
                <ZoomableMedia key={i} label={`Variation ${i + 1}`}>
                  <figure className="rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900/40">
                    <img
                      src={url}
                      alt={`Variation ${i + 1}`}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                    <figcaption className="px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-zinc-400 border-t border-zinc-800/70">
                      Variation {i + 1}
                    </figcaption>
                  </figure>
                </ZoomableMedia>
              ))}
            </div>
          )}
          {ready && !msg.streaming && (msg.content || "").length > 400 && (
            <button
              type="button"
              data-testid={`msg-copy-${msg.id}`}
              onClick={() => copyReply(msg.content || "")}
              className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-400 border border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-300 px-2.5 py-1.5 rounded-md transition-colors"
            >
              <Copy className="w-3 h-3" /> Copy full reply
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Strip the assistant's "copy everything below" lead-in and closing question so
// what lands in the clipboard is only the deliverable (e.g. the Lovable prompt).
const stripReplyChrome = (text) => {
  const lines = text.trim().split("\n");
  if (/copy everything below/i.test(lines[0] || "")) lines.shift();
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  if (/^would you like to change anything/i.test((lines[lines.length - 1] || "").trim())) lines.pop();
  return lines.join("\n").trim();
};

const copyReply = async (content) => {
  const text = stripReplyChrome(content);
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error("no-clipboard-api");
    }
  } catch (_e) {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (!ok) {
      toast.error("Copy failed. Select the text and copy manually.");
      return;
    }
  }
  toast.success("Copied — paste it into Lovable.");
};


const TypingBubble = () => (
  <div className="flex gap-3">
    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mt-1">
      <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
    </div>
    <div className="beam-left pl-5 inline-flex items-center gap-1.5 py-3">
      <span className="typing-dot"></span>
      <span className="typing-dot"></span>
      <span className="typing-dot"></span>
    </div>
  </div>
);

const EmptyChat = ({ tool, onStarter }) => {
  const starters = tool?.conversation_starters || [];
  return (
    <div className="text-center py-12">
      <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-5">
        <Sparkles className="w-6 h-6 text-emerald-400" />
      </div>
      <h3 className="font-display text-3xl tracking-tighter font-black">
        {tool?.name}
      </h3>
      <p className="text-zinc-400 mt-2 max-w-md mx-auto text-sm">
        {tool?.description}
      </p>
      {starters.length > 0 ? (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
          {starters.map((s, i) => (
            <button
              key={s}
              data-testid={`starter-${i}`}
              onClick={() => onStarter(s)}
              className="text-left px-4 py-3 rounded-xl border border-zinc-800 bg-zinc-900/40 hover:border-emerald-500/40 hover:bg-emerald-500/5 transition-colors group"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400/70 mb-1 group-hover:text-emerald-400">
                Starter · {String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-sm text-zinc-200 leading-snug">{s}</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="mt-6 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          Type below to begin
        </div>
      )}
    </div>
  );
};

const LockedView = ({ tool }) => (
  <div className="flex-1 flex items-center justify-center p-12">
    <div className="text-center max-w-md">
      <div className="w-16 h-16 mx-auto rounded-2xl bg-amber-500/10 border border-amber-500/40 flex items-center justify-center mb-5 glow-gold">
        <Lock className="w-7 h-7 text-amber-300" />
      </div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
        // restricted
      </div>
      <h3 className="font-display text-3xl tracking-tighter font-black text-white">
        {tool?.name} is locked
      </h3>
      <p className="text-zinc-400 mt-3">
        This GPT is locked. Please unlock access by contacting your admin.
      </p>
      <Link
        to="/dashboard"
        className="inline-block mt-6 text-emerald-400 hover:text-emerald-300 text-sm uppercase tracking-[0.2em]"
      >
        ← Back to dashboard
      </Link>
    </div>
  </div>
);


/**
 * PostCraftVariationSlot — inline placeholder for a PostCraft variation
 * image that hasn't been generated yet.
 *
 * Three visual states:
 *   1. Auto-generation still in flight (streaming or V1 not yet arrived) —
 *      "Generating variation N…"
 *   2. On-demand slot (V2/V3, or V1 after auto-failure) — shows a
 *      "Generate this variation" button that hits `POST /generate-image`.
 *      While the request is in flight (queued/retrying) we show
 *      "generating… (high demand, retrying)".
 *   3. Failure — soft error message + retry button. Never surfaces a raw
 *      503 from Gemini to the user.
 */
const PostCraftVariationSlot = ({ variation, pending, messageId, sessionId, streaming, onImageReady }) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Streaming still in progress and no on-demand info yet → wait for the
  // backend to decide whether this variation is auto or on-demand.
  if (streaming && !pending) {
    return (
      <div className="my-4 rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-6 text-center">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          Variation {variation}
        </div>
        <div className="text-sm text-zinc-400 mt-1 italic">
          Generating…
        </div>
      </div>
    );
  }

  const canGenerate = !!pending && !!pending.image_prompt;

  const generate = async () => {
    if (!canGenerate || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const res = await api.post("/generate-image", {
        session_id: sessionId,
        image_prompt: pending.image_prompt,
        aspect_ratio: pending.aspect_ratio || "1:1",
        message_id: pending.message_id || messageId || undefined,
        variation,
      });
      const url = res?.data?.image_base64;
      if (!url) throw new Error("empty_response");
      onImageReady(url);
    } catch (e) {
      // Never leak a raw 503 — show the soft retry state.
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`variation-slot-${variation}`}
      className="my-4 rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-6 flex flex-col items-center justify-center text-center gap-3"
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        Variation {variation}
      </div>
      {busy ? (
        <div className="text-sm text-zinc-400 italic flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-emerald-500/60 border-t-transparent animate-spin" />
          generating… (high demand, retrying)
        </div>
      ) : failed ? (
        <>
          <div className="text-sm text-zinc-400">
            Couldn't generate — tap to retry
          </div>
          <button
            type="button"
            data-testid={`variation-retry-${variation}`}
            onClick={generate}
            disabled={!canGenerate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-40"
          >
            Retry variation {variation}
          </button>
        </>
      ) : (
        <>
          <div className="text-xs text-zinc-500 max-w-md">
            {canGenerate
              ? "This variation is available on-demand — the image is generated only when you ask, so you never wait for what you might not use."
              : "Waiting for prompt…"}
          </div>
          <button
            type="button"
            data-testid={`variation-generate-${variation}`}
            onClick={generate}
            disabled={!canGenerate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Generate this variation
          </button>
        </>
      )}
    </div>
  );
};
